<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
/* eslint-disable prettier/prettier, vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text */
import { computed, inject, proxyRefs, ref } from 'vue';
import ImudeiRuntimeActionPanel from './ImudeiRuntimeActionPanel.vue';
import { useImudeiRuntimeModel } from './useImudeiRuntimeModel';

const emit = defineEmits(['navigate']);
const realData = inject('imudeiRealData');
const { error, saving } = realData;
const model = proxyRefs(
  useImudeiRuntimeModel({
    realData,
    module: computed(() => 'proposals'),
    screen: computed(() => 'edit'),
  })
);

const source = computed(() => model.detail?.source || {});
const editingOfferAmount = ref(false);
const editingPropertyLink = ref(false);
const editingContactLink = ref(false);
const firstValue = (value, paths, fallback = '') => {
  const result = paths
    .map(path => path.split('.').reduce((item, key) => item?.[key], value))
    .find(item => item !== undefined && item !== null && item !== '');

  return result ?? fallback;
};
const sourceValue = paths => firstValue(source.value, paths);
const imageUrlFrom = value => {
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) return imageUrlFrom(value[0]);
  if (!value || typeof value !== 'object') return '';

  return imageUrlFrom(
    firstValue(value, [
      'url',
      'src',
      'image_url',
      'imageUrl',
      'preview_url',
      'file_url',
      'thumbnail_url',
      'attachment_url',
    ])
  );
};
const initials = name =>
  String(name || '—')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(item => item[0])
    .join('')
    .toUpperCase();
const updateField = (key, event) => {
  model.form[key] = event?.target?.value ?? event;
};
const status = computed(() => model.detail?.status || 'Em edição');
const activeStage = computed(() => {
  const value = String(status.value).toLocaleLowerCase('pt-BR');

  if (/final|aceit|conclu/.test(value)) return 4;
  if (/contrat|assin/.test(value)) return 3;
  if (/doc/.test(value)) return 4;

  return 2;
});
const stageState = index => {
  if (index < activeStage.value) return 'done';
  if (index === activeStage.value) return 'current';

  return 'pending';
};
const stages = computed(() =>
  [
    ['i-lucide-house', 'Imóvel', 'Dados do imóvel', 0],
    ['i-lucide-user-round', 'Interessado', 'Dados do cliente', 1],
    ['i-lucide-circle-dollar-sign', 'Valores e condições', 'Valores da proposta', 2],
    ['i-lucide-file-text', 'Condições', 'Regras e prazos', 3],
    ['i-lucide-file-check-2', 'Documentos', 'Anexos da proposta', 4],
  ].map(stage => ({
    icon: stage[0],
    label: stage[1],
    subtitle: stage[2],
    state: stageState(stage[3]),
  }))
);
const propertyImage = computed(() =>
  imageUrlFrom(
    firstValue(source.value, [
      'imovel.cover_url',
      'imovel.image_url',
      'imovel.thumbnail',
      'imovel.cover',
      'imovel.photo_url',
      'imovel.photos',
      'imovel.images',
      'imovel.midias',
      'imovel.media',
      'property.cover_url',
      'property.image_url',
      'property.thumbnail',
      'property.cover',
      'property.photo_url',
      'property.photos',
      'property.images',
      'property.media',
      'listing.cover_url',
      'listing.image_url',
      'listing.thumbnail',
      'listing.photo_url',
      'listing.photos',
      'listing.images',
      'listing.media',
      'image_url',
      'images',
      'media',
    ])
  )
);
const proposalCode = computed(() =>
  firstValue(source.value, ['reference', 'code'], `#${model.detail?.id || '—'}`)
);
const proposalTitle = computed(
  () =>
    model.detail?.title ||
    firstValue(source.value, ['imovel_titulo', 'imovel.title'], 'Proposta')
);
const propertyAddress = computed(() =>
  firstValue(source.value, [
    'imovel_endereco',
    'imovel.endereco',
    'property.address',
    'listing.address',
  ])
);
const propertyLinkDisplay = computed(
  () =>
    firstValue(source.value, ['imovel_titulo', 'imovel.titulo']) ||
    model.form.imovel_id ||
    ''
);
const contactLinkDisplay = computed(
  () =>
    firstValue(source.value, ['contact_name', 'contact.name', 'buyer.name']) ||
    model.form.contact_id ||
    ''
);
const participants = computed(() => {
  const candidates = [
    source.value.contact && { ...source.value.contact, role: 'Interessado' },
    source.value.buyer && { ...source.value.buyer, role: 'Interessado' },
    source.value.user_name && {
      id: source.value.user_id,
      name: source.value.user_name,
      role: 'Corretor responsável',
    },
    source.value.user,
    source.value.broker,
    source.value.responsible,
    ...(Array.isArray(source.value.participants)
      ? source.value.participants
      : []),
  ].filter(Boolean);

  return candidates
    .map((participant, index) => {
      const name = firstValue(participant, [
        'name',
        'full_name',
        'contact.name',
        'user.name',
      ]);
      if (!name) return null;

      return {
        avatar: firstValue(participant, [
          'avatar_url',
          'thumbnail',
          'contact.thumbnail',
          'user.avatar_url',
        ]),
        id: participant.id || `${name}-${index}`,
        initials: initials(name),
        name,
        role: firstValue(
          participant,
          ['role', 'party_role_label', 'kind'],
          index ? 'Participante' : 'Interessado'
        ),
      };
    })
    .filter(Boolean)
    .filter(
      (participant, index, list) =>
        list.findIndex(item => item.name === participant.name) === index
    )
    .slice(0, 4);
});
const fieldByKey = computed(() =>
  Object.fromEntries(model.fields.map(field => [field.key, field]))
);
const fieldFor = key => fieldByKey.value[key];
const notesField = computed(() =>
  model.fields.find(field => field.key === 'notes')
);
const revision = computed(
  () => source.value.current_revision || source.value.currentRevision || {}
);
const offerTimeline = computed(() => {
  const candidates = [
    source.value.timeline,
    source.value.timeline_payload?.steps,
    source.value.timelinePayload?.steps,
  ];
  const populatedTimeline = candidates.find(
    timeline => Array.isArray(timeline) && timeline.length
  );

  return populatedTimeline || [];
});
const currentTimelineIndex = computed(() => {
  const currentKey = firstValue(source.value, [
    'timeline_payload.current_step.key',
    'timelinePayload.current_step.key',
    'current_step.key',
    'step.key',
  ]);
  const keyedIndex = offerTimeline.value.findIndex(step => step.key === currentKey);
  if (keyedIndex >= 0) return keyedIndex;

  const index = offerTimeline.value.findIndex(step => step.current);
  return index >= 0 ? index : 0;
});
const currentTimelineStep = computed(
  () => offerTimeline.value[currentTimelineIndex.value] || {}
);
const progressStepCount = computed(() =>
  offerTimeline.value.length || stages.value.length
);
const completedTimelineSteps = computed(() =>
  offerTimeline.value.filter(step => step.state === 'concluido').length
);
const progressStepNumber = computed(() =>
  offerTimeline.value.length
    ? Math.max(completedTimelineSteps.value, currentTimelineIndex.value + 1)
    : activeStage.value + 1
);
const currentTimelineDescription = computed(
  () =>
    currentTimelineStep.value.description ||
    firstValue(source.value, [
      'timeline_payload.macro_status_label',
      'timelinePayload.macro_status_label',
      'macro_status_label',
    ]) || String(currentTimelineStep.value.state || '').replaceAll('_', ' ')
);
const revisionValue = (key, fallbackPaths = []) => {
  const current = revision.value?.[key];
  if (current !== undefined && current !== null && current !== '') return current;

  const difference = revision.value?.diff?.[key];
  if (difference && typeof difference === 'object') {
    const value = firstValue(difference, ['to', 'after', 'current', 'value']);
    if (value !== '') return value;
  }
  if (difference !== undefined && difference !== null && difference !== '') {
    return difference;
  }

  return sourceValue(fallbackPaths);
};
const money = value => {
  if (value === '' || value === null || value === undefined) return '—';
  const number = Number(value);
  if (Number.isNaN(number)) return String(value);

  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 2,
  }).format(number);
};
const offerAmountDisplay = computed(() => {
  const formatted = money(model.form.offer_amount);
  return formatted === '—' ? '' : formatted.replace(/^R\$\s*/, '');
});
const updateOfferAmount = event => {
  const value = String(event?.target?.value || '');
  const numericValue = value.replace(/[^\d,.-]/g, '');

  model.form.offer_amount = numericValue.includes(',')
    ? numericValue.replace(/\./g, '').replace(',', '.')
    : numericValue;
};
const date = value => {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.valueOf())) return String(value);

  return new Intl.DateTimeFormat('pt-BR').format(parsed);
};
const readable = value => {
  if (value === '' || value === null || value === undefined) return '—';
  if (typeof value === 'boolean') return value ? 'Sim' : 'Não';
  if (Array.isArray(value)) return value.length ? `${value.length} itens` : '—';
  if (typeof value === 'object') return '—';

  return String(value).replaceAll('_', ' ');
};
const hasValue = value =>
  value !== '' && value !== null && value !== undefined && value !== '—';
const optionLabel = value => {
  const labels = {
    a_vista: 'À vista',
    a_vista_fgts: 'À vista com FGTS',
    booking: 'Reserva',
    financiamento: 'Financiamento',
    financiamento_fgts: 'Financiamento com FGTS',
    offer: 'Proposta',
    purchase: 'Compra',
    rental: 'Locação',
    sale: 'Venda',
    aluguel: 'Locação',
    venda: 'Venda',
  };

  return labels[String(value)] || readable(value);
};
const fact = (paths, format = readable) => format(sourceValue(paths));
const percentageOfOffer = value => {
  const offerAmount = Number(model.form.offer_amount);
  const amount = Number(value);

  if (!Number.isFinite(offerAmount) || offerAmount <= 0 || !Number.isFinite(amount)) {
    return '—';
  }

  return `${new Intl.NumberFormat('pt-BR', {
    maximumFractionDigits: 2,
  }).format((amount / offerAmount) * 100)} %`;
};
const revisionPreviousValue = key => {
  const difference = revision.value?.diff?.[key];
  if (difference && typeof difference === 'object') {
    const value = firstValue(difference, [
      'before',
      'from',
      'previous',
      'old',
      'old_value',
    ]);
    if (value !== '') return value;
  }

  return firstValue(source.value, [
    `previous_revision.${key}`,
    `previous_${key}`,
  ]);
};
const financialFacts = computed(() => {
  const entryAmount = revisionValue('entry_amount', ['entry_amount']);
  const financedAmount = revisionValue('financed_amount', ['financed_amount']);
  const installmentCount = revisionValue('installment_count', [
    'installment_count',
  ]);
  const paymentKind = revisionValue('payment_kind', ['payment_kind']);
  const businessContext = sourceValue(['business_context']);
  const propertyPurpose = sourceValue(['imovel_finalidade', 'imovel.finalidade']);

  return [
    hasValue(entryAmount)
      ? {
          label: 'Sinal / Entrada',
          ratio: percentageOfOffer(entryAmount),
          value: money(entryAmount),
        }
      : {
          label: 'Forma de pagamento',
          ratio: revisionValue('currency', ['currency']),
          value: optionLabel(paymentKind),
        },
    hasValue(financedAmount)
      ? {
          label: 'Financiamento',
          ratio: percentageOfOffer(financedAmount),
          value: money(financedAmount),
        }
      : {
          label: 'Contexto comercial',
          ratio: optionLabel(propertyPurpose),
          value: optionLabel(businessContext),
        },
    hasValue(installmentCount)
      ? {
          label: 'Parcelas',
          suffix: 'meses',
          value: readable(installmentCount),
        }
      : {
          label: 'Finalidade do imóvel',
          suffix: '',
          value: optionLabel(propertyPurpose),
        },
  ];
});
const conditionFields = computed(() =>
  ['payment_kind', 'business_context', 'kind']
    .map(fieldFor)
    .filter(Boolean)
);
const activities = computed(() =>
  (Array.isArray(source.value.negotiation_timeline) &&
  source.value.negotiation_timeline.length
    ? source.value.negotiation_timeline
    : model.history
  )
    .slice(-3)
    .reverse()
    .map((item, index) => {
      const action = firstValue(item, ['action', 'label', 'event_type']);
      const actionCopy = {
        accept: 'Proposta aceita',
        counter: 'Contraproposta registrada',
        decline: 'Proposta recusada',
        expire: 'Proposta expirada',
        submit: 'Nova proposta enviada',
        withdraw: 'Proposta retirada',
      };
      const transition = [item.from_status, item.to_status]
        .filter(Boolean)
        .join(' → ');

      return {
        actor:
          String(item.actor_id) === String(source.value.user_id)
            ? source.value.user_name
            : firstValue(
                item,
                ['actor.name', 'author.name', 'user.name', 'performed_by.name', 'actor_side', 'actor'],
                ''
              ),
        date: date(firstValue(item, ['occurred_at', 'created_at', 'updated_at', 'date'])),
        detail:
          firstValue(item, [
            'notes',
            'description',
            'details',
            'message',
            'metadata.description',
          ]) || transition,
        icon:
          {
            accept: 'i-lucide-badge-check',
            counter: 'i-lucide-file-pen-line',
            decline: 'i-lucide-circle-x',
            submit: 'i-lucide-send',
            withdraw: 'i-lucide-undo-2',
          }[action] || 'i-lucide-history',
        id: item.id || item.sequence || `${action}-${index}`,
        label: actionCopy[action] || item.label || 'Atualização da proposta',
        metric: firstValue(item, ['points', 'reward_points', 'metadata.points'], '—'),
        tone:
          {
            accept: 'bg-emerald-50 text-emerald-600',
            counter: 'bg-violet-50 text-violet-600',
            decline: 'bg-rose-50 text-rose-600',
            submit: 'bg-sky-50 text-sky-600',
            withdraw: 'bg-amber-50 text-amber-600',
          }[action] || 'bg-violet-50 text-violet-600',
      };
    })
);
const additionalTerms = computed(() => {
  const terms =
    revision.value?.terms ||
    revision.value?.diff?.terms?.to ||
    revision.value?.diff?.terms ||
    source.value.terms ||
    {};

  if (!terms || Array.isArray(terms) || typeof terms !== 'object') return [];

  return Object.entries(terms)
    .filter(([, value]) => ['string', 'number', 'boolean'].includes(typeof value))
    .slice(0, 3)
    .map(([key, value]) => ({
      label: key.replaceAll('_', ' '),
      value: String(value),
    }));
});
const comparisonRows = computed(() => {
  const rows = [
    ['offer_amount', 'Valor proposto', model.form.offer_amount, 'money'],
    [
      'payment_kind',
      'Forma de pagamento',
      revisionValue('payment_kind', ['payment_kind']),
      'label',
    ],
    [
      'business_context',
      'Contexto comercial',
      sourceValue(['business_context']),
      'label',
    ],
    [
      'imovel_titulo',
      'Imóvel vinculado',
      propertyLinkDisplay.value,
      'text',
    ],
    [
      'contact_name',
      'Interessado',
      contactLinkDisplay.value,
      'text',
    ],
    [
      'proposed_at',
      'Data da proposta',
      revisionValue('proposed_at', ['proposal_date', 'submitted_at', 'created_at']),
      'date',
    ],
    [
      'expires_at',
      'Validade da proposta',
      revisionValue('expires_at', ['expires_at']),
      'date',
    ],
    ['notes', 'Observações', model.form.notes, 'text'],
  ];

  return rows.map(([key, label, current, type]) => {
    const formatter = { date, label: optionLabel, money, text: readable };
    const format = formatter[type];
    const previous = revisionPreviousValue(key);

    return {
      changed: previous !== '' && String(previous) !== String(current),
      current: format(current),
      label,
      previous: previous === '' ? '—' : format(previous),
    };
  });
});
const versionLabel = computed(() => {
  const version = firstValue(source.value, [
    'current_version_number',
    'current_revision.version_number',
    'current_revision.sequence',
    'version_number',
  ]);

  return version === '' ? 'Versão atual' : `v${version}`;
});
const originalVersionLabel = computed(() => {
  const version = firstValue(source.value, [
    'previous_version_number',
    'current_revision.previous_version_number',
  ]);

  if (version !== '') return `v${version}`;
  const sequence = Number(revision.value?.sequence);

  return Number.isFinite(sequence) && sequence > 1
    ? `v${sequence - 1}`
    : 'Origem';
});
const progress = computed(() =>
  Math.max(1, Math.round((progressStepNumber.value / progressStepCount.value) * 100))
);
const progressClass = computed(() => {
  if (progress.value >= 80) return 'w-[86%]';
  if (progress.value >= 70) return 'w-[72%]';
  if (progress.value >= 60) return 'w-2/3';
  if (progress.value >= 50) return 'w-[58%]';
  if (progress.value >= 40) return 'w-1/2';
  if (progress.value >= 25) return 'w-[30%]';

  return 'w-[14%]';
});
const saveState = computed(() =>
  model.notice
    ? {
        copy: 'A resposta da API confirmou a última alteração.',
        icon: 'i-lucide-check',
        title: 'Alterações salvas',
        tone: 'bg-emerald-500 text-white',
      }
    : {
        copy: 'Nenhuma alteração foi enviada para a API.',
        icon: 'i-lucide-shield-check',
        title: 'Pronto para salvar',
        tone: 'bg-[#eee6ff] text-[#5a24c7]',
      }
);
const submit = async () => {
  const response = await model.saveEdit();
  if (response) emit('navigate', 'detail');
};
</script>

<template>
  <main
    data-imudei-visual-id="proposal-edit-root"
    class="mx-auto w-full max-w-[1500px] px-5 pb-36 pt-4 text-[#11184f]"
  >
    <header class="flex items-start justify-between gap-6 pl-10">
      <div>
        <p class="text-[11px] font-medium text-[#59638d]">
          Propostas <span class="mx-2 text-[#a6adbf]">›</span>{{ proposalCode }}
          <span class="mx-2 text-[#a6adbf]">›</span>Editar
        </p>
        <h1 class="mt-1 text-[23px] font-semibold tracking-[-0.04em] text-[#111746]">
          Editar proposta
        </h1>
        <p class="mt-1 text-[11px] text-[#65719a]">
          Revise e edite os valores e condições desta proposta. As alterações seguem o contrato real.
        </p>
      </div>

      <div class="flex min-w-[633px] items-center justify-between gap-3 pt-1">
        <span class="mr-3 flex items-center gap-2 text-[10px] font-medium text-emerald-600">
          <span class="i-lucide-circle-check size-4" />Dados sincronizados com a API
        </span>
        <a
          href="#proposal-version"
          class="flex h-10 items-center gap-2 rounded-lg border border-[#e1e3ef] bg-white px-4 text-[10px] font-semibold text-[#4d1ed1] shadow-[0_2px_8px_rgba(62,37,141,0.02)]"
        >
          <span class="i-lucide-scale size-4" />Comparar mudanças
        </a>
        <button
          class="flex h-10 items-center gap-2 rounded-lg border border-[#e1e3ef] bg-white px-4 text-[10px] font-semibold text-[#4d1ed1] shadow-[0_2px_8px_rgba(62,37,141,0.02)]"
          type="button"
          @click="emit('navigate', 'detail')"
        >
          <span class="i-lucide-eye size-4" />Visualizar proposta
        </button>
      </div>
    </header>

    <section class="-ml-5 mt-0 grid w-[calc(100%+20px)] grid-cols-[225px_minmax(0,1fr)_412px] items-start gap-[21px]">
      <aside
        data-imudei-visual-id="proposal-edit-steps"
        class="-ml-[34px] mt-[6px] flex h-[694px] w-[250px] flex-col"
      >
        <article class="overflow-hidden rounded-xl border border-[#e6e9f3] bg-white p-2 shadow-[0_4px_18px_rgba(72,49,144,0.02)]">
          <div
            v-for="stage in stages"
            :key="stage.label"
            class="flex min-h-[62px] items-center gap-3 rounded-lg px-3"
            :class="stage.state === 'current' ? 'border border-[#cbbcff] bg-[#fcfbff]' : ''"
          >
            <span
              class="grid size-8 shrink-0 place-items-center rounded-full"
              :class="
                stage.state === 'current'
                  ? 'bg-[#531bdc] text-white shadow-[0_5px_12px_rgba(83,27,220,0.24)]'
                  : stage.state === 'done'
                    ? 'border border-[#dce1ee] bg-white text-[#15205e]'
                    : 'border border-[#e1e5ef] bg-white text-[#5d6993]'
              "
            >
              <span :class="stage.icon" class="size-4" />
            </span>
            <span class="min-w-0 flex-1">
              <strong
                class="block text-[10px] font-semibold"
                :class="stage.state === 'current' ? 'text-[#531bdc]' : ''"
              >
                {{ stage.label }}
              </strong>
              <small class="mt-0.5 block text-[9px] text-[#6c789f]">
                {{ stage.subtitle }}
              </small>
            </span>
            <span
              v-if="stage.state === 'done'"
              class="i-lucide-circle-check size-4 text-emerald-500"
            />
            <span
              v-else-if="stage.state === 'current'"
              class="rounded bg-[#eee5ff] px-2 py-1 text-[8px] font-semibold text-[#5421c7]"
            >
              Atual
            </span>
            <span v-else class="size-4 rounded-full border border-[#b9c1d6]" />
          </div>
        </article>

        <article class="mt-5 min-h-[174px] rounded-xl border border-[#e6e9f3] bg-white p-4 shadow-[0_4px_18px_rgba(72,49,144,0.02)]">
          <header class="flex items-center justify-between">
            <strong class="text-[11px]">Missão em andamento</strong>
            <span class="text-[8px] font-bold text-[#5a24c7]">{{ progress }}%</span>
          </header>
          <div class="mt-4 flex items-center gap-3">
            <span class="grid size-12 shrink-0 place-items-center rounded-full border-[3px] border-[#d8cefa] text-[11px] font-bold text-[#5521cf]">
              {{ progress }}%
            </span>
            <span>
              <strong class="block text-[10px]">
                {{ currentTimelineStep.label || stages[activeStage].label }}
              </strong>
              <small class="mt-1 block text-[9px] leading-4 text-[#68749a]">
                {{ currentTimelineDescription || stages[activeStage].subtitle }}
              </small>
            </span>
          </div>
          <div class="mt-4 h-1.5 overflow-hidden rounded-full bg-[#eceaf1]">
            <span
              class="block h-full rounded-full bg-gradient-to-r from-[#4e18ec] to-[#df28d5]"
              :class="progressClass"
            />
          </div>
        </article>

        <article class="mt-auto min-h-[76px] rounded-xl border border-[#f2d477] bg-[#fffaf0] p-4">
          <div class="flex gap-3">
            <span class="i-lucide-lightbulb size-5 shrink-0 text-[#f5b51f]" />
            <span>
              <strong class="block text-[10px]">Edição não gera pontos</strong>
              <small class="mt-1 block text-[9px] leading-4 text-[#6d7190]">
                Você só ganha pontos ao validar, avançar etapas e concluir marcos reais.
              </small>
            </span>
          </div>
        </article>
      </aside>

      <form class="space-y-[11px] pt-[6px]" @submit.prevent="submit">
        <article
          data-imudei-visual-id="proposal-edit-progress"
          class="h-[59px] rounded-xl border border-[#e6e9f3] bg-white px-5 py-2 shadow-[0_4px_18px_rgba(72,49,144,0.02)]"
        >
          <div class="flex items-center justify-between text-[10px]">
            <strong class="text-[11px] font-semibold text-[#172050]">Progresso da proposta</strong>
            <span class="font-medium text-[#5724c9]">
              {{ progressStepNumber }} de {{ progressStepCount }} etapas
            </span>
          </div>
          <div class="mt-3 flex items-center gap-4">
            <div class="h-1.5 flex-1 overflow-hidden rounded-full bg-[#eceaf1]">
              <span
                class="block h-full rounded-full bg-gradient-to-r from-[#4c15ec] via-[#9a32ee] to-[#e82fd2]"
                :class="progressClass"
              />
            </div>
            <span class="flex items-center gap-2 border-l border-[#e8e5ee] pl-4 text-[10px] font-semibold text-[#db2bb5]">
              <span class="i-lucide-sparkles size-4" />{{ progress }}%
            </span>
          </div>
        </article>

        <article
          data-imudei-visual-id="proposal-edit-form"
          class="h-[629px] overflow-y-auto rounded-xl border border-[#e6e9f3] bg-white p-5 pb-1 shadow-[0_4px_18px_rgba(72,49,144,0.02)]"
        >
          <header>
            <h2 class="text-[18px] font-semibold tracking-[-0.025em] text-[#121949]">Valores e condições</h2>
            <h3 class="mt-3 text-[11px] font-semibold text-[#1c2455]">Valores da proposta</h3>
          </header>

          <div v-if="fieldFor('offer_amount')" class="mt-1 grid grid-cols-2 gap-3">
            <label class="h-[54px] rounded-lg border border-[#e1e5f1] px-3 py-2 text-[10px] font-medium text-[#66739c] focus-within:border-[#8a5ae8]">
              {{ fieldFor('offer_amount').label }}<b class="text-rose-500"> *</b>
              <span class="mt-1 flex items-center gap-1 text-[12px] font-semibold text-[#17235d]">
                <span class="text-[10px]">R$</span>
                <input
                  inputmode="decimal"
                  type="text"
                  :value="editingOfferAmount ? model.form.offer_amount : offerAmountDisplay"
                  class="!h-5 !min-h-0 min-w-0 flex-1 appearance-none !border-0 !bg-transparent !p-0 !shadow-none outline-none ring-0 focus:!border-0 focus:outline-none focus:ring-0"
                  :required="fieldFor('offer_amount').required"
                  @blur="editingOfferAmount = false"
                  @focus="editingOfferAmount = true"
                  @input="updateOfferAmount"
                />
              </span>
            </label>
            <div class="h-[54px] rounded-lg border border-[#e1e5f1] px-3 py-2 text-[10px] font-medium text-[#66739c]">
              {{ financialFacts[0].label }}
              <span class="mt-1 flex items-center justify-between gap-2 text-[12px] font-semibold text-[#17235d]">
                {{ financialFacts[0].value }}
                <small class="border-l border-[#e9e7f0] pl-3 text-[10px] font-medium text-[#59658d]">
                  {{ financialFacts[0].ratio }}
                </small>
              </span>
            </div>
            <div class="h-[54px] rounded-lg border border-[#e1e5f1] px-3 py-2 text-[10px] font-medium text-[#66739c]">
              {{ financialFacts[1].label }}
              <span class="mt-1 flex items-center justify-between gap-2 text-[12px] font-semibold text-[#17235d]">
                {{ financialFacts[1].value }}
                <small class="border-l border-[#e9e7f0] pl-3 text-[10px] font-medium text-[#59658d]">
                  {{ financialFacts[1].ratio }}
                </small>
              </span>
            </div>
            <div class="h-[54px] rounded-lg border border-[#e1e5f1] px-3 py-2 text-[10px] font-medium text-[#66739c]">
              {{ financialFacts[2].label }}
              <span class="mt-1 flex items-center justify-between gap-2 text-[12px] font-semibold text-[#17235d]">
                {{ financialFacts[2].value }}
                <small
                  v-if="financialFacts[2].suffix"
                  class="rounded border border-[#e5e6f0] px-2 py-0.5 text-[8px] font-medium text-[#65719a]"
                >
                  {{ financialFacts[2].suffix }}
                </small>
              </span>
            </div>
          </div>
          <p v-else class="mt-3 rounded-lg bg-[#fbfaff] p-3 text-[9px] text-[#6c789f]">
            Nenhum campo editável foi disponibilizado pelo backend.
          </p>

          <div class="mt-2 grid grid-cols-3 gap-3">
            <div class="h-[54px] rounded-lg border border-[#e1e5f1] px-3 py-2 text-[10px] font-medium text-[#66739c]">
              Validade da proposta
              <span class="mt-1 flex items-center gap-2 text-[12px] font-semibold text-[#17235d]">
                <span class="i-lucide-calendar-days size-3.5 text-[#65719a]" />
                {{ fact(['current_revision.expires_at', 'expires_at'], date) }}
              </span>
            </div>
            <label
              v-if="fieldFor('imovel_id')"
              class="h-[54px] rounded-lg border border-[#e1e5f1] px-3 py-2 text-[10px] font-medium text-[#66739c] focus-within:border-[#8a5ae8]"
            >
              Imóvel vinculado<b v-if="fieldFor('imovel_id').required" class="text-rose-500"> *</b>
              <input
                inputmode="numeric"
                type="text"
                :value="editingPropertyLink ? model.form.imovel_id : propertyLinkDisplay"
                class="mt-1 !h-5 !min-h-0 w-full appearance-none !border-0 !bg-transparent !p-0 !text-[12px] !font-semibold !text-[#17235d] !shadow-none outline-none ring-0 focus:!border-0 focus:outline-none focus:ring-0"
                :required="fieldFor('imovel_id').required"
                @blur="editingPropertyLink = false"
                @focus="editingPropertyLink = true"
                @input="updateField('imovel_id', $event)"
              />
            </label>
            <label
              v-if="fieldFor('contact_id')"
              class="h-[54px] rounded-lg border border-[#e1e5f1] px-3 py-2 text-[10px] font-medium text-[#66739c] focus-within:border-[#8a5ae8]"
            >
              Interessado vinculado<b v-if="fieldFor('contact_id').required" class="text-rose-500"> *</b>
              <input
                inputmode="numeric"
                type="text"
                :value="editingContactLink ? model.form.contact_id : contactLinkDisplay"
                class="mt-1 !h-5 !min-h-0 w-full appearance-none !border-0 !bg-transparent !p-0 !text-[12px] !font-semibold !text-[#17235d] !shadow-none outline-none ring-0 focus:!border-0 focus:outline-none focus:ring-0"
                :required="fieldFor('contact_id').required"
                @blur="editingContactLink = false"
                @focus="editingContactLink = true"
                @input="updateField('contact_id', $event)"
              />
            </label>
          </div>

          <div class="mt-4 h-20 overflow-y-auto rounded-lg border border-[#e1e5f1] bg-[#fcfbff] p-2">
            <div class="flex items-center justify-between">
              <strong class="text-[9px] font-semibold text-[#172050]">Condições comerciais</strong>
              <span class="text-[8px] text-[#79659a]">Dados reais</span>
            </div>
            <div class="mt-2 flex flex-wrap gap-2">
              <label
                v-for="field in conditionFields"
                :key="field.key"
                :title="field.label"
                class="rounded-md border border-[#d8ccfb] bg-white px-2 py-0 text-[9px] text-[#5922c8]"
              >
                <select
                  :value="model.form[field.key]"
                  class="!h-5 !min-h-0 appearance-none !border-0 !bg-transparent !p-0 !pr-3 !text-[9px] !font-medium !text-[#5922c8] !shadow-none outline-none focus:!border-0 focus:outline-none focus:ring-0"
                  :required="field.required"
                  @change="updateField(field.key, $event)"
                >
                  <option value="">Selecione</option>
                  <option
                    v-for="option in field.options || []"
                    :key="option"
                    :value="option"
                  >
                    {{ optionLabel(option) }}
                  </option>
                </select>
              </label>
              <span
                v-for="term in additionalTerms"
                :key="term.label"
                class="rounded-md border border-[#d8ccfb] bg-white px-2 py-0 text-[9px] text-[#5922c8]"
              >
                {{ term.label }}: {{ term.value }}
              </span>
              <span
                v-if="!conditionFields.length && !additionalTerms.length"
                class="text-[8px] text-[#68749a]"
              >
                Sem condições adicionais retornadas.
              </span>
            </div>
          </div>

          <div v-if="notesField" class="mt-3">
            <label class="text-[11px] font-semibold text-[#172050]">Observações e condições</label>
            <div class="mt-1 overflow-hidden rounded-lg border border-[#e1e5f1]">
              <div class="flex h-8 items-center gap-4 border-b border-[#e9e7f0] px-3 text-[10px] text-[#55608c]">
                <span class="font-bold">B</span>
                <span class="italic">I</span>
                <span class="underline">U</span>
                <span class="i-lucide-list size-3.5" />
                <span class="i-lucide-list-ordered size-3.5" />
                <span class="i-lucide-link size-3.5" />
              </div>
              <textarea
                :value="model.form[notesField.key]"
                class="min-h-[80px] w-full !border-0 !bg-transparent px-3 py-2 text-[10px] leading-5 !text-[#4d5a85] !shadow-none outline-none focus:!border-0 focus:outline-none focus:ring-0"
                :required="notesField.required"
                @input="updateField(notesField.key, $event)"
              />
            </div>
          </div>

          <div class="mt-3">
            <div class="flex items-center justify-between">
              <h3 class="text-[11px] font-semibold text-[#172050]">Participantes desta proposta</h3>
              <span class="text-[9px] font-semibold text-[#5922c8]">
                {{ participants.length }} vinculados
              </span>
            </div>
            <div v-if="participants.length" class="mt-1 grid grid-cols-3 gap-3">
              <div
                v-for="participant in participants"
                :key="participant.id"
                class="flex min-w-0 items-center gap-2 rounded-lg border border-[#d8ccfb] p-1.5"
              >
                <img
                  v-if="participant.avatar"
                  :src="participant.avatar"
                  :alt="participant.name"
                  class="size-8 rounded-full object-cover"
                />
                <span
                  v-else
                  class="grid size-8 place-items-center rounded-full bg-[#eee6ff] text-[8px] font-bold text-[#5922c8]"
                >
                  {{ participant.initials }}
                </span>
                <span class="min-w-0 flex-1">
                  <strong class="block truncate text-[9px] text-[#172050]">{{ participant.name }}</strong>
                  <small class="mt-0.5 inline-flex max-w-full truncate rounded bg-[#f0e9ff] px-1.5 py-0.5 text-[8px] font-medium text-[#6734c7]">
                    {{ participant.role }}
                  </small>
                </span>
                <span
                  class="i-lucide-x size-3.5 shrink-0 text-[#667098]"
                  title="A API atual não disponibiliza remoção de participantes nesta tela."
                />
              </div>
              <span
                class="flex items-center justify-center gap-2 rounded-lg border border-dashed border-[#bda9ff] px-3 text-[9px] font-semibold text-[#5922c8]"
                title="A API atual não disponibiliza inclusão de participantes nesta tela."
              >
                <span class="i-lucide-plus size-4" />Adicionar participante
              </span>
            </div>
            <p v-else class="mt-2 rounded-lg bg-[#fbfaff] p-3 text-[9px] text-[#6c789f]">
              Nenhum participante foi retornado pela API.
            </p>
          </div>
        </article>

        <ImudeiRuntimeActionPanel
          v-if="model.actionOptions.length || model.transitionOptions.length"
          :action-options="model.actionOptions"
          :can-counter-offer="model.actionOptions.some(action => action.name === 'counter')"
          :form="model.form"
          :saving="saving"
          :transition-options="model.transitionOptions"
          @run="model.runAction"
          @update-field="updateField"
        />
        <p
          v-if="model.unavailableMessage"
          class="rounded-xl border border-violet-100 bg-violet-50 px-4 py-3 text-[9px] text-[#6540a8]"
        >
          {{ model.unavailableMessage }}
        </p>
        <p
          v-if="model.localError || error"
          class="rounded-xl border border-rose-100 bg-rose-50 px-4 py-3 text-[9px] text-rose-600"
        >
          {{ model.localError || error }}
        </p>
        <p
          v-if="model.notice"
          class="rounded-xl border border-emerald-100 bg-emerald-50 px-4 py-3 text-[9px] text-emerald-700"
        >
          {{ model.notice }}
        </p>
      </form>

      <aside data-imudei-visual-id="proposal-edit-sidebar" class="-mt-[11px] space-y-[11px]">
        <article
          id="proposal-version"
          class="h-[454px] overflow-hidden rounded-xl border border-[#e6e9f3] bg-white p-4 shadow-[0_4px_18px_rgba(72,49,144,0.02)]"
        >
          <h2 class="text-[15px] font-semibold tracking-[-0.02em] text-[#121949]">
            Alterações nesta versão <span v-if="versionLabel !== 'Versão atual'">({{ versionLabel }})</span>
          </h2>
          <img
            v-if="propertyImage"
            :src="propertyImage"
            :alt="proposalTitle"
            class="mt-1.5 h-[108px] w-full rounded-lg object-cover"
          />
          <div
            v-else
            class="relative mt-1.5 h-[108px] overflow-hidden rounded-lg bg-gradient-to-r from-[#5f3b25] via-[#d9c39a] to-[#d9e3d7] text-white"
          >
            <span class="absolute inset-y-0 left-0 w-[31%] bg-gradient-to-br from-[#70462a] via-[#35251a] to-[#1e1814]" />
            <span class="absolute inset-y-0 left-[31%] w-px bg-white/65" />
            <span class="absolute inset-y-0 left-[54%] w-px bg-white/55" />
            <span class="absolute inset-y-0 left-[77%] w-px bg-white/55" />
            <span class="absolute inset-x-0 top-[53%] h-px bg-white/55" />
            <span class="absolute -right-3 top-4 size-16 rounded-full bg-[#4c7756]/55 blur-xl" />
            <span class="absolute right-10 top-11 size-14 rounded-full bg-[#6c944c]/45 blur-xl" />
            <span class="absolute bottom-0 left-[31%] right-0 h-7 bg-gradient-to-t from-[#b19c7a]/70 to-transparent" />
            <span class="absolute left-3 top-3 max-w-[26%] truncate text-[9px] font-semibold text-white/85">
              {{ proposalTitle }}
            </span>
            <small
              v-if="propertyAddress"
              class="absolute bottom-3 left-3 max-w-[26%] truncate text-[8px] text-white/65"
            >
              {{ propertyAddress }}
            </small>
          </div>
          <div class="mt-3 grid grid-cols-[1fr_1fr_1fr_12px] gap-2 border-b border-[#e9e7f0] pb-2 text-[9px] font-semibold text-[#68749b]">
            <span>Campo</span>
            <span>Versão anterior ({{ originalVersionLabel }})</span>
            <span>Versão atual ({{ versionLabel }})</span>
            <span />
          </div>
          <div
            v-for="row in comparisonRows"
            :key="row.label"
            class="grid grid-cols-[1fr_1fr_1fr_12px] gap-2 border-b border-[#eeedf4] py-2 text-[9px] leading-4"
          >
            <span class="truncate text-[#52618c]">{{ row.label }}</span>
            <span class="truncate text-[#68749a]">{{ row.previous }}</span>
            <strong class="truncate font-semibold text-[#0aa95a]">{{ row.current }}</strong>
            <span
              class="size-3"
              :class="row.changed ? 'i-lucide-arrow-up text-emerald-600' : 'i-lucide-minus text-[#a5a0b1]'"
            />
          </div>
        </article>

        <article class="flex h-[251px] flex-col overflow-hidden rounded-xl border border-[#e6e9f3] bg-white p-4 shadow-[0_4px_18px_rgba(72,49,144,0.02)]">
          <header class="flex items-center justify-between">
            <h2 class="text-[14px] font-semibold tracking-[-0.02em] text-[#121949]">Feed de atividades</h2>
          </header>
          <div v-if="activities.length" class="mt-1">
            <div
              v-for="activity in activities"
              :key="activity.id"
              class="mt-3 flex items-start gap-2 border-b border-[#eeedf4] pb-3 first:mt-0 last:border-0"
            >
              <span
                class="grid size-5 shrink-0 place-items-center rounded-full"
                :class="activity.tone"
              >
                <span :class="activity.icon" class="size-3" />
              </span>
              <span class="min-w-0 flex-1">
                <span class="flex min-w-0 items-baseline gap-1">
                  <strong class="truncate text-[9px] text-[#172050]">{{ activity.actor }}</strong>
                  <span class="truncate text-[9px] text-[#44527f]">{{ activity.label }}</span>
                </span>
                <small v-if="activity.detail" class="mt-1 block truncate text-[8px] text-[#6c789f]">
                  {{ activity.detail }}
                </small>
              </span>
              <span class="flex shrink-0 flex-col items-end text-[8px] text-[#6c789f]">
                <small :class="activity.metric === '—' ? '' : 'font-semibold text-emerald-600'">
                  {{ activity.metric === '—' ? '—' : `${activity.metric} pts` }}
                </small>
                <small>{{ activity.date }}</small>
              </span>
            </div>
          </div>
          <p v-else class="mt-3 px-1 text-[9px] text-[#6c789f]">
            —
          </p>
          <span
            v-if="activities.length"
              class="mt-auto flex items-center justify-center gap-2 text-[9px] font-semibold text-[#5922c8]"
          >
            Ver todas as atividades <span class="i-lucide-arrow-right size-3.5" />
          </span>
        </article>
      </aside>
    </section>

    <footer
      data-imudei-visual-id="proposal-edit-footer"
      class="fixed bottom-4 left-5 right-5 z-50 flex h-[104px] items-center rounded-xl border border-[#e6e9f3] bg-white/95 px-5 shadow-[0_-8px_25px_rgba(38,25,86,0.04)] backdrop-blur"
    >
      <div class="flex min-w-[375px] items-center gap-3 border-r border-[#ebe9f2] pr-5">
        <span class="grid size-9 place-items-center rounded-full" :class="saveState.tone">
          <span :class="saveState.icon" class="size-5" />
        </span>
        <span>
          <strong class="block text-[10px] text-[#172050]">{{ saveState.title }}</strong>
          <small class="text-[9px] text-[#68749a]">
            {{ saveState.copy }}
          </small>
        </span>
      </div>
      <div class="ml-5 grid w-[560px] flex-none grid-cols-[150px_repeat(4,minmax(0,1fr))] items-center gap-3">
        <span>
          <strong class="block text-[9px] text-[#172050]">Como ganhar pontos nesta proposta</strong>
          <small class="mt-1 block text-[8px] text-[#7b85a4]">—</small>
        </span>
        <span
          v-for="stage in stages.slice(0, 4)"
          :key="stage.label"
          class="flex min-w-0 items-center gap-1.5"
        >
          <span
            class="grid size-6 shrink-0 place-items-center rounded-md border border-[#e1e2ef] text-[#5a24c7]"
          >
            <span :class="stage.icon" class="size-3" />
          </span>
          <span class="min-w-0">
            <small class="block truncate text-[8px] font-semibold leading-3 text-[#1ca78c]">— pts</small>
            <small
              class="block truncate text-[8px] leading-3"
              :class="stage.state === 'current' ? 'font-semibold text-[#5a24c7]' : 'text-[#7580a0]'"
            >
              {{ stage.label }}
            </small>
          </span>
        </span>
      </div>
      <div class="ml-auto flex items-center gap-3">
        <button
          class="h-11 min-w-[100px] rounded-lg border border-[#8b68ef] bg-white px-5 text-[10px] font-semibold text-[#5421c7]"
          type="button"
          @click="emit('navigate', 'detail')"
        >
          Cancelar
        </button>
        <button
          class="h-11 min-w-[136px] rounded-lg border border-[#ddd4fb] bg-white px-5 text-[10px] font-semibold text-[#a39bb4]"
          type="button"
          disabled
          title="O backend atual não disponibiliza rascunhos de propostas."
        >
          Salvar rascunho
        </button>
        <button
          class="flex h-11 min-w-[212px] items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-[#4f16df] via-[#6c18df] to-[#c51bcf] px-6 text-[10px] font-bold text-white shadow-[0_7px_16px_rgba(91,24,211,0.18)] disabled:cursor-not-allowed disabled:opacity-50"
          type="submit"
          :disabled="saving || !model.canSubmit"
        >
          <span class="i-lucide-save size-4" />
          {{ saving ? 'Salvando…' : 'Salvar nova versão' }}
        </button>
      </div>
    </footer>
  </main>
</template>
